import { useEffect, useState } from "react"
import { Star, Loader2 } from "lucide-react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { motion, useAnimation } from "framer-motion"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import axios from "@/utils/axios"

export default function CustomerReviews() {
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)

  // Default reviews as fallback
  const defaultReviews = [
    {
      id: 1,
      name: "Sarah L.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Absolutely Transformed My Skin!",
      review:
        "Fayoz products have transformed my skin! My complexion is clearer and more radiant than ever before. Highly recommend the Hydrating Face Cream!",
      date: new Date().toISOString(),
    },
    {
      id: 2,
      name: "David P.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 4,
      title: "Gentle and Effective Cleanser",
      review:
        "I've struggled with sensitive skin for years, but Fayoz's Gentle Cleanser is a game-changer. No irritation, just clean, soft skin. Fantastic!",
      date: new Date().toISOString(),
    },
    {
      id: 3,
      name: "Emily R.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Youth in a Bottle!",
      review:
        "The Anti-Aging Serum is incredible! I've noticed a significant reduction in fine lines around my eyes. It feels luxurious and works wonders.",
      date: new Date().toISOString(),
    },
    {
      id: 4,
      name: "Michael B.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Best Sunscreen Ever!",
      review:
        "Finally, a sunscreen that doesn't feel greasy! The SPF 30 Sunscreen is lightweight and provides excellent protection. A daily essential for me.",
      date: new Date().toISOString(),
    },
  ]

  // Fetch reviews from API
  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true)
      try {
        const res = await axios.get("/reviews", {
          params: {
            page: 1,
            limit: 10, // Get more reviews for better scrolling effect
          },
        })
        const fetchedReviews = res.data.reviews || []
        // Use fetched reviews if available, otherwise use defaults
        setReviews(fetchedReviews.length > 0 ? fetchedReviews : defaultReviews)
      } catch (error) {
        console.error("Error fetching reviews:", error)
        // Use default reviews on error
        setReviews(defaultReviews)
      } finally {
        setLoading(false)
      }
    }
    fetchReviews()
  }, [])

  // Duplicate reviews for seamless looping (only if we have reviews)
  const duplicatedReviews = reviews.length > 0 ? [...reviews, ...reviews] : []

  const controls = useAnimation()

  useEffect(() => {
    if (duplicatedReviews.length > 0) {
      const scroll = async () => {
        await controls.start({
          x: ["0%", "-50%"],
          transition: {
            duration: 40,
            ease: "linear",
            repeat: Infinity,
          },
        })
      }
      scroll()
    }
  }, [controls, duplicatedReviews.length])

  return (
    <section className="py-16 overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-4">What Our Customers Say</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Hear from our happy customers who have experienced the Fayoz difference.
          </p>
        </motion.div>

        <div className="relative w-full overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : duplicatedReviews.length > 0 ? (
            <motion.div
              className="flex gap-6 w-max"
              animate={controls}
            >
              {duplicatedReviews.map((review, index) => (
                <div
                  key={`${review.id}-${index}`}
                  className="min-w-[320px] max-w-[320px] flex-shrink-0"
                >
                  <Card className="shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
                    <CardHeader className="flex flex-row items-center gap-4 pb-2">
                      <Avatar>
                        <AvatarImage src={review.avatar || "/placeholder.svg"} alt={review.name} />
                        <AvatarFallback>{review.name?.charAt(0) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-semibold truncate">{review.name}</CardTitle>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                            />
                          ))}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2 flex-1">
                      {review.title && (
                        <h4 className="font-medium text-gray-900 mb-2 text-sm">{review.title}</h4>
                      )}
                      <p className="text-gray-700 text-sm">{review.review}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12 text-gray-600">
              No reviews available at the moment.
            </div>
          )}
        </div>

        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Button asChild variant="outline" className="px-8 py-6 text-lg bg-transparent">
            <Link to="/reviews">Read All Reviews</Link>
          </Button>
        </motion.div>
      </div>
    </section>
  )
}
